import { alpha } from "@mui/material/styles";

export const CUSTOMER_PANEL_ACCENTS = {
  client: "#00a58a",
  contacts: "#3b9fd4",
  docs: "#6b7fd7",
  activities: "#8b7fd6",
  projects: "#e8956f",
};

export const createCustomerPanelUi = (theme, accent, options = {}) => {
  const { variant = "column" } = options;
  const isColumn = variant === "column";

  const panelSx = {
    width: "100%",
    minWidth: "100%",
    maxWidth: "100%",
    height: { xs: "auto", lg: "calc(100dvh - 120px)" },
    minHeight: { xs: 320, lg: "calc(100dvh - 120px)" },
    display: "flex",
    flexDirection: "column",
    border: "none",
    borderRadius: 0,
    overflow: "hidden",
    background: "transparent",
    boxShadow: "none",
    ...(isColumn
      ? {
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
          "&:last-of-type": { borderRight: "none" },
        }
      : {}),
  };

  const headerSx = {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: alpha(theme.palette.background.paper, 0.92),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    px: 1.5,
    py: 1.1,
    minHeight: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    boxSizing: "border-box",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 999,
      bgcolor: accent,
    },
  };

  const headerEyebrowSx = {
    display: "block",
    fontSize: "0.62rem",
    fontWeight: 800,
    letterSpacing: 0.55,
    textTransform: "uppercase",
    color: accent,
    lineHeight: 1.2,
    mb: 0.15,
  };

  const headerTitleSx = {
    fontWeight: 800,
    fontSize: "0.95rem",
    lineHeight: 1.15,
    color: "text.primary",
  };

  const headerSubtitleSx = {
    mt: 0.2,
    fontSize: "0.74rem",
    color: "text.secondary",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  };

  const addButtonSx = {
    fontWeight: 700,
    fontSize: "0.78rem",
    padding: "4px 12px",
    minWidth: 68,
    borderRadius: 2,
    textTransform: "none",
    border: "1px solid",
    borderColor: alpha(accent, 0.35),
    color: accent,
    bgcolor: alpha(accent, 0.06),
    boxShadow: "none",
    "&:hover": {
      bgcolor: alpha(accent, 0.12),
      borderColor: accent,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      borderColor: alpha(theme.palette.divider, 0.8),
      color: theme.palette.text.disabled,
      bgcolor: alpha(theme.palette.action.disabledBackground, 0.4),
    },
  };

  const saveButtonSx = {
    fontWeight: 800,
    fontSize: "0.78rem",
    padding: "5px 14px",
    minWidth: 88,
    borderRadius: 2,
    textTransform: "none",
    bgcolor: accent,
    color: "#fff",
    boxShadow: `0 4px 14px ${alpha(accent, 0.28)}`,
    "&:hover": {
      bgcolor: alpha(accent, 0.9),
      boxShadow: `0 6px 18px ${alpha(accent, 0.32)}`,
    },
    "&.Mui-disabled": {
      bgcolor: alpha(theme.palette.action.disabledBackground, 0.8),
      color: theme.palette.text.disabled,
      boxShadow: "none",
    },
  };

  const primaryButtonSx = addButtonSx;

  const bodySx = {
    flex: 1,
    overflowY: "auto",
    px: 1.5,
    py: 1.25,
    background: "transparent",
    scrollbarWidth: "thin",
  };

  const cardSx = {
    border: "none",
    borderRadius: 2.5,
    background: alpha(theme.palette.background.paper, 0.88),
    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      background: theme.palette.background.paper,
      boxShadow: `0 4px 14px ${alpha(accent, 0.08)}`,
    },
  };

  const cardTitleSx = {
    fontWeight: 700,
    fontSize: "0.86rem",
    lineHeight: 1.25,
    color: "text.primary",
  };

  const bodyTextSx = {
    fontSize: "0.8rem",
    lineHeight: 1.45,
    color: "text.secondary",
  };

  const metaTextSx = {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: "text.secondary",
  };

  const pillSx = {
    px: 0.65,
    py: 0.2,
    borderRadius: 999,
    border: "none",
    backgroundColor: alpha(accent, 0.1),
    fontSize: "0.64rem",
    fontWeight: 700,
    color: alpha(accent, 0.95),
    display: "inline-flex",
    alignItems: "center",
    gap: 0.35,
  };

  const avatarSx = {
    width: 34,
    height: 34,
    borderRadius: "50%",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: "0.72rem",
    color: accent,
    border: "none",
    background: alpha(accent, 0.12),
  };

  const actionIconSx = {
    border: "none",
    height: 30,
    width: 30,
    borderRadius: 1.5,
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
    color: "text.secondary",
    "&:hover": {
      backgroundColor: alpha(accent, 0.1),
      color: accent,
    },
  };

  const metricTileSx = {
    borderRadius: 2,
    border: "none",
    backgroundColor: alpha(accent, 0.06),
    px: 0.75,
    py: 0.55,
  };

  const sectionTitleSx = {
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: 0.45,
    textTransform: "uppercase",
    color: "text.secondary",
    mb: 0.85,
    mt: 0.25,
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      borderRadius: 1.5,
      backgroundColor: alpha(theme.palette.background.paper, 0.98),
      fontSize: "0.84rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.72),
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(accent, 0.4),
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.84rem",
      fontWeight: 600,
    },
    "& .MuiChip-root": {
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      borderRadius: 1,
      bgcolor: alpha(accent, 0.1),
      color: accent,
    },
  };

  const emptyStateSx = {
    py: 3,
    px: 1.5,
    textAlign: "center",
    borderRadius: 2.5,
    border: "1px dashed",
    borderColor: alpha(theme.palette.divider, 0.85),
    bgcolor: alpha(theme.palette.background.default, 0.45),
    color: "text.secondary",
    fontSize: "0.82rem",
    fontWeight: 600,
  };

  return {
    panelSx,
    headerSx,
    headerEyebrowSx,
    headerTitleSx,
    headerSubtitleSx,
    addButtonSx,
    saveButtonSx,
    primaryButtonSx,
    bodySx,
    cardSx,
    cardTitleSx,
    bodyTextSx,
    metaTextSx,
    pillSx,
    avatarSx,
    actionIconSx,
    metricTileSx,
    sectionTitleSx,
    fieldSx,
    emptyStateSx,
  };
};
